import { useMemo } from "react";
import "instantsearch.css/themes/satellite.css";
import "./SupplierInventorySearch.css";
import "./RefinementList.css";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Text,
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
  ModalFooter,
} from "@chakra-ui/react";
import { BsFillInboxesFill } from "react-icons/bs";
import { BsX, BsCheck } from "react-icons/bs";
import { AMBER_COLORS } from "./config";
import { formatDate, formatTime } from "../../utils/dateResolvers";

const getModalStyles = (isFromTopSuppliers: boolean) => ({
  headerBox: {
    bgGradient: isFromTopSuppliers
      ? `linear(to-r, white, ${AMBER_COLORS[200]})`
      : "linear(to-r, #1976f2, #5b2be0)",
    px: 6,
    py: 4,
    position: "relative" as const,
    borderTopRadius: "xl",
    borderBottom: isFromTopSuppliers ? "1px solid" : "none",
    borderColor: isFromTopSuppliers ? AMBER_COLORS[100] : "transparent",
  },
  modalBody: {
    px: 6,
    py: 4,
    flex: 1,
    overflowY: "auto" as const,
    bg: "#fbfbfb",
    fontFamily: "'Nunito','Inter', sans-serif",
    sx: {
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
  },
  tableBox: {
    border: "1px solid",
    borderColor: "gray.200",
    borderRadius: "lg",
    boxShadow: "xs",
    p: 4,
    bg: "white",
  },
  modalFooter: {
    bg: "#fffdf9",
    borderTop: "1px solid",
    borderColor: "gray.200",
    justifyContent: "flex-end",
    gap: 3,
    position: "sticky" as const,
    bottom: 0,
    zIndex: 1,
  },
});

type Part = {
  rootPartNumber: string;
  partNumber: string;
  quantity: number;
  id?: string;
};

type SupplierPart = {
  rootPartNumber: string;
  familyQuantity: number;
  id: string;
  updatedAt?: number;
};

type CustomHitsProps = {
  preFacetedPartNumbers: Part[];
  finalSupplierPartsMap: SupplierPart[];
  isFromTopSuppliers: boolean;
};

const CustomHits = ({
  preFacetedPartNumbers,
  finalSupplierPartsMap,
  isFromTopSuppliers,
}: CustomHitsProps) => {
  const supplierPartsMap = useMemo(() => {
    return new Map(
      finalSupplierPartsMap.map((part) => [
        part.rootPartNumber,
        {
          id: part.id,
          familyQuantity: part.familyQuantity,
          updatedAt: part.updatedAt,
        },
      ])
    );
  }, [finalSupplierPartsMap]);

  // Memoize the rows to prevent unnecessary re-renders
  const rows = useMemo(() => {
    let index = 0;
    return preFacetedPartNumbers
      .map((prefetchedPart) => {
        const matchingPart = supplierPartsMap.get(
          prefetchedPart.rootPartNumber
        );

        if (!matchingPart) {
          return null;
        }
        return (
          <Tr
            key={matchingPart.id}
            bg={index % 2 !== 0 ? "gray.50" : "white"}
            fontSize="sm"
          >
            <Td textAlign="center" color="gray.700">
              {(index = index + 1)}
            </Td>
            <Td textAlign="center" fontWeight="bold" color="gray.700">
              {prefetchedPart.partNumber}
            </Td>
            <Td textAlign="center" color="gray.700">
              {prefetchedPart.quantity}
            </Td>
            <Td textAlign="center" color="gray.700">
              {matchingPart.familyQuantity ?? "N/A"}
            </Td>
            <Td textAlign="center" color="gray.700">
              {matchingPart.updatedAt
                ? `${formatDate(matchingPart.updatedAt)}, ${formatTime(
                    matchingPart.updatedAt
                  )}`
                : "—"}
            </Td>
          </Tr>
        );
      })
      .filter(Boolean);
  }, [preFacetedPartNumbers, supplierPartsMap]);

  return <>{rows}</>;
};

const SupplierDetailsModal = ({
  supplierLabel,
  isOpen,
  onClose,
  preFacetedPartNumbers,
  finalHitMap,
  handleSelectListItem,
  isFromTopSuppliers = false,
  onSelectionClose,
  isEstimateOrder = false,
}: {
  supplierLabel: string;
  isOpen: boolean;
  onClose: () => void;
  preFacetedPartNumbers: any;
  typesenseInstantsearchAdapter: any;
  finalHitMap: any;
  handleSelectListItem: (
    item: { _id: string; supplierName: string },
    supplierParts?: any[]
  ) => void;
  onSelectionClose: () => void;
  isFromTopSuppliers?: boolean;
  isEstimateOrder?: boolean;
}) => {
  const finalSupplierPartsMap = useMemo(() => {
    return finalHitMap.get(supplierLabel) ?? [];
  }, [finalHitMap, supplierLabel]);

  const modalStyles = getModalStyles(isFromTopSuppliers);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      scrollBehavior="inside"
      isCentered
    >
      <ModalOverlay />
      <ModalContent
        borderRadius="xl"
        overflow="hidden"
        maxHeight="85vh"
        display="flex"
        flexDirection="column"
        boxShadow={"md"}
      >
        {/* Header */}
        <Box {...modalStyles.headerBox}>
          <ModalCloseButton
            color={"white"}
            top={3}
            right={4}
            _hover={{
              background: "white",
              color: isFromTopSuppliers ? AMBER_COLORS[600] : "blue.500",
            }}
          />
          <Box>
            <Text
              fontSize="lg"
              fontWeight="bold"
              color={isFromTopSuppliers ? AMBER_COLORS[700] : "white"}
            >
              {supplierLabel.split("::")[0]}
            </Text>
            <Text
              fontSize="sm"
              fontWeight="500"
              color={
                isFromTopSuppliers
                  ? AMBER_COLORS[600]
                  : "rgba(255, 255, 255, 0.64)"
              }
            >
              Detailed Information
            </Text>
          </Box>
        </Box>

        {/* Body */}
        <ModalBody {...modalStyles.modalBody}>
          <Box {...modalStyles.tableBox}>
            <Text
              fontSize="md"
              fontWeight="semibold"
              mb={3}
              display="flex"
              alignItems="center"
              gap={2}
              color={"gray.700"}
            >
              <Icon
                as={BsFillInboxesFill}
                color={isFromTopSuppliers ? AMBER_COLORS[600] : "blue.500"}
                boxSize={4}
              />
              Part Numbers and Quantities
            </Text>

            <Box overflowX="auto" fontFamily="'Nunito','Inter', sans-serif">
              <Table size="md" variant="unstyled">
                <Thead bg={"gray.100"}>
                  <Tr>
                    <Th textAlign="center" fontSize="md" color={"gray.600"}>
                      #
                    </Th>
                    <Th textAlign="center" fontSize="md" color={"gray.600"}>
                      Part #
                    </Th>
                    <Th textAlign="center" fontSize="md" color={"gray.600"}>
                      Ordered Quantity
                    </Th>
                    <Th textAlign="center" fontSize="md" color={"gray.600"}>
                      Supplier Quantity
                    </Th>
                    <Th textAlign="center" fontSize="md" color={"gray.600"}>
                      Last Updated
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <CustomHits
                    preFacetedPartNumbers={preFacetedPartNumbers}
                    finalSupplierPartsMap={finalSupplierPartsMap}
                    isFromTopSuppliers={isFromTopSuppliers}
                  />
                </Tbody>
              </Table>
            </Box>
          </Box>
        </ModalBody>

        {/* Footer */}
        <ModalFooter {...modalStyles.modalFooter}>
          <Button
            variant="ghost"
            color={isFromTopSuppliers ? AMBER_COLORS[700] : "gray.700"}
            leftIcon={<Icon as={BsX} />}
            onClick={onClose}
            _hover={{
              bg: isFromTopSuppliers ? AMBER_COLORS[50] : "gray.100",
              color: isFromTopSuppliers ? AMBER_COLORS[600] : "gray.800",
            }}
            fontSize={{
              base: "xs",
              "2xl": "md",
            }}
          >
            Close
          </Button>
          <Button
            backgroundSize="200%"
            transition="background-position 0.3s ease"
            backgroundPosition="left center"
            color={isFromTopSuppliers ? AMBER_COLORS[700] : "white"}
            bgGradient={
              isFromTopSuppliers
                ? `linear(to-l,${AMBER_COLORS[200]},  ${AMBER_COLORS[50]}, ${AMBER_COLORS[200]})`
                : "linear(to-r,#5b2be0, #1976f2, #5b2be0)"
            }
            leftIcon={<Icon as={BsCheck} />}
            fontSize={{
              base: "xs",
              "2xl": "md",
            }}
            _hover={{
              backgroundPosition: "right center",
            }}
            onClick={() => {
              const supplierId = supplierLabel.split("::")[1];
              const supplierName = supplierLabel.split("::")[0];
              handleSelectListItem(
                {
                  _id: supplierId,
                  supplierName: supplierName,
                },
                finalHitMap.get(supplierLabel)
              );
              onSelectionClose();
              onClose();
            }}
          >
            {isEstimateOrder ? "View Estimate" : "Select Supplier"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
export default SupplierDetailsModal;
